import { Fragment, type FunctionComponent, useContext } from 'react';

import { ApplicationContext } from '~/shared/context/context';
import { Button } from '~/ui/core/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '~/ui/core/dropdown-menu/DropdownMenu';

import { InviteTeamMemberDialog } from '../../../company-settings/tabs/organization-tab/users-and-roles/InviteTeamMemberDialog';
import { JOB_ARCHITECTURE_INVITATION_ROLES, useInviteTeamMember } from '../../api/use-invite-team-member';
import type { JobItemDetails } from '../../api/use-job-item-details';
import { GenericReviewerContextMenuComponent } from '../../components/ReviewerComponents';
import { getJobTypeTranslation } from '../../hooks/use-review-action';
import { ButtonContainer, Container, Text, TextContainer, variants } from './notification-bar-styled';

export const AssignReviewerNotification: FunctionComponent<{
    readonly architectureId: string;
    readonly item: JobItemDetails;
    readonly itemType: 'job-level' | 'job-role';
}> = ({ architectureId, item, itemType }) => {
    const { translate } = useContext(ApplicationContext);
    const { availableMethods, closeInviteDialog, handleInvite, isInviteDialogOpen, openInviteDialog } =
        useInviteTeamMember({
            architectureId,
            itemId: item.id,
            itemType,
        });

    if (!item.actions?.canAssignReviewer) {
        return null;
    }

    const jobType = getJobTypeTranslation(itemType, translate);

    return (
        <Fragment>
            <Container
                key="reviewer"
                animate={variants.animate}
                data-testid="assign-reviewer-notification"
                exit={variants.exit}
                initial={variants.initial}
            >
                <TextContainer>
                    <Text>{translate('pages.job-architecture-v2.job-add-reviewer-notification', { jobType })}</Text>
                </TextContainer>
                <ButtonContainer>
                    <DropdownMenu>
                        <DropdownMenuTrigger>
                            <Button variant="primary">{translate('pages.job-architecture-v2.job-add-reviewer')}</Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" collisionPadding={16} side="bottom">
                            <GenericReviewerContextMenuComponent
                                item={item}
                                itemType={itemType}
                                jobArchitectureId={architectureId}
                                jobClusterId={item.parentId}
                                onlyAssignReviewer={true}
                                onInvite={openInviteDialog}
                            />
                        </DropdownMenuContent>
                    </DropdownMenu>
                </ButtonContainer>
            </Container>
            <InviteTeamMemberDialog
                allowedRoles={JOB_ARCHITECTURE_INVITATION_ROLES}
                availableMethods={availableMethods}
                open={isInviteDialogOpen}
                title={translate('pages.company-settings.users-and-roles.invite-dialog.assign-review-title')}
                onClose={closeInviteDialog}
                onInvite={handleInvite}
            />
        </Fragment>
    );
};
